import React, { useState, useMemo } from "react";
import {
  deleteDoc,
  doc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { 
  FiRefreshCw, FiTrash2, FiCheckCircle, FiXCircle, FiCalendar, 
  FiUser, FiSearch, FiFilter, FiChevronLeft, FiChevronRight,
  FiClock, FiTrendingUp, FiAlertCircle, FiUsers
} from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { useColorMode, Tooltip, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Button, useDisclosure } from "@chakra-ui/react";
import { toLocalDateString } from "../../../utils/dateUtils";
import { Floor } from "./ManagerFloorView";
import { showToast } from "../../../utils/toastUtils";

interface Booking {
  id: string;
  seatId: string;
  seatName: string;
  floorId: string;
  floorName: string;
  userId: string;
  userName: string;
  date: string;
  status: 'active' | 'cancelled' | 'completed' | 'pending';
  createdAt: any;
}

interface ManagerBookingListProps {
  bookings: Booking[];
  floors: Floor[];
  currentOrgId: string;
  onRefresh: () => void;
  users?: any[];
}

const ManagerBookingList: React.FC<ManagerBookingListProps> = ({ bookings, floors, currentOrgId, onRefresh, users = [] }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMonth, setViewMonth] = useState<Date>(new Date());
  const [viewYear, setViewYear] = useState<number>(new Date().getFullYear());
  const [filterFloor, setFilterFloor] = useState<string>("All Floors");
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active'>('all');
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Confirmation Dialog
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [actionBooking, setActionBooking] = useState<{id: string, action: 'approve' | 'reject'} | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Helper to get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(viewMonth);
  const firstDayOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();

  // Calendar dates generation
  const calendarDays = useMemo(() => {
    const days = [];
    // Empty slots for previous month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    // Days of current month
    for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i));
    }
    return days;
  }, [viewMonth, daysInMonth, firstDayOfMonth]);

  // Counts for calendar indicators
  const bookingCountsByDate = useMemo(() => {
      const counts: Record<string, { total: number, pending: number, active: number }> = {};
      bookings.forEach(b => {
          if(b.status === 'cancelled') return;
          const d = b.date;
          if(!counts[d]) counts[d] = { total: 0, pending: 0, active: 0 };
          counts[d].total++;
          if(b.status === 'pending') counts[d].pending++;
          if(b.status === 'active') counts[d].active++;
      });
      return counts;
  }, [bookings]);

  // Statistics for current month view
  const selectedDateStats = useMemo(() => {
    // Filter bookings for the current viewing month
    const monthBookings = bookings.filter(b => {
      if (b.status === 'cancelled') return false;
      const bookingDate = new Date(b.date);
      const currentMonth = viewMonth.getMonth();
      const currentYear = viewMonth.getFullYear();
      return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
    });
    
    const pending = monthBookings.filter(b => b.status === 'pending').length;
    const active = monthBookings.filter(b => b.status === 'active').length;
    const totalSeats = floors.reduce((sum, f) => sum + (f.seats?.length || 0), 0);
    const utilization = totalSeats > 0 ? Math.round((active / totalSeats) * 100) : 0;
    
    return {
      total: monthBookings.length,
      pending,
      active,
      utilization
    };
  }, [viewMonth, bookings, floors]);

  const handleMonthChange = (increment: number) => {
      const newDate = new Date(viewMonth);
      newDate.setMonth(newDate.getMonth() + increment);
      setViewMonth(newDate);
      setViewYear(newDate.getFullYear());
  };

  const handleYearChange = (increment: number) => {
      const newYear = viewYear + increment;
      setViewYear(newYear);
      const newDate = new Date(viewMonth);
      newDate.setFullYear(newYear);
      setViewMonth(newDate);
  };

  const handleApprove = async (bookingId: string) => {
      try {
          await updateDoc(doc(db, `organization/${currentOrgId}/bookings`, bookingId), {
              status: 'active'
          });
          onRefresh();
          showToast({ title: "Booking Approved", status: "success", duration: 2000 });
      } catch (e) {
          showToast({ title: "Failed to approve", status: "error" });
      }
  };

  const handleReject = async (bookingId: string) => {
      setActionBooking({ id: bookingId, action: 'reject' });
      onOpen();
  };

  const confirmAction = async () => {
      if(!actionBooking) return;
      
      try {
          if(actionBooking.action === 'reject') {
             await updateDoc(doc(db, `organization/${currentOrgId}/bookings`, actionBooking.id), {
                 status: 'cancelled'
             });
             showToast({ title: "Booking Rejected", status: "info", duration: 2000 });
          }
          onRefresh();
          onClose();
      } catch (e) {
          showToast({ title: "Action failed", status: "error" });
      }
  };

  const handleApproveAll = async () => {
      if (!selectedDate) return;
      
      const pendingBookings = enrichedBookings.filter(b => b.status === 'pending');
      if (pendingBookings.length === 0) {
         showToast({ title: "No pending bookings to approve", status: "info", duration: 2000 });
         return;
      }

      try {
         await Promise.all(
            pendingBookings.map(booking => 
               updateDoc(doc(db, `organization/${currentOrgId}/bookings`, booking.id), {
                  status: 'active'
               })
            )
         );
         onRefresh();
         showToast({ 
            title: `Approved ${pendingBookings.length} booking${pendingBookings.length > 1 ? 's' : ''}`, 
            status: "success", 
            duration: 2000 
         });
      } catch (e) {
         showToast({ title: "Failed to approve all bookings", status: "error" });
      }
   };

   const handleRejectAll = async () => {
      if (!selectedDate) return;
      
      const pendingBookings = enrichedBookings.filter(b => b.status === 'pending');
      if (pendingBookings.length === 0) {
         showToast({ title: "No pending bookings to reject", status: "info", duration: 2000 });
         return;
      }

      try {
         await Promise.all(
            pendingBookings.map(booking => 
               updateDoc(doc(db, `organization/${currentOrgId}/bookings`, booking.id), {
                  status: 'cancelled'
               })
            )
         );
         onRefresh();
         showToast({ 
            title: `Rejected ${pendingBookings.length} booking${pendingBookings.length > 1 ? 's' : ''}`, 
            status: "info", 
            duration: 2000 
         });
      } catch (e) {
         showToast({ title: "Failed to reject all bookings", status: "error" });
      }
   };

  const filteredBookings = bookings.filter(b => {
      // If no date selected, show only current month's bookings
      if (!selectedDate) {
         const bookingDate = new Date(b.date);
         const currentMonth = viewMonth.getMonth();
         const currentYear = viewMonth.getFullYear();
         if (bookingDate.getMonth() !== currentMonth || bookingDate.getFullYear() !== currentYear) {
            return false;
         }
      } else if (b.date !== toLocalDateString(selectedDate)) {
         return false;
      }
      
      if (b.status === 'cancelled') return false;
      if (filterFloor !== "All Floors" && b.floorName !== filterFloor) return false;
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const user = users.find(u => u.id === b.userId);
        const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.toLowerCase();
        const userEmail = (user?.email || '').toLowerCase();
        const seatName = b.seatName.toLowerCase();
        
        if (!userName.includes(query) && !userEmail.includes(query) && !seatName.includes(query)) {
          return false;
        }
      }
      
      return true;
  });
  
  
  // Enrich with User Data and memoize
  const enrichedBookings = useMemo(() => {
    return filteredBookings.map(b => {
        const user = users.find(u => u.id === b.userId);
        return {
            ...b,
            userFirstName: user?.firstName || 'Unknown',
            userLastName: user?.lastName || '',
            userEmail: user?.email || '',
            userRole: user?.role || 'User'
        };
    }).sort((a, b) => {
        // Sort by date descending (most recent first)
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [filteredBookings, users]);

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped: Record<string, typeof enrichedBookings> = {};
    enrichedBookings.forEach(booking => {
      if (!grouped[booking.date]) {
        grouped[booking.date] = [];
      }
      grouped[booking.date].push(booking);
    });
    return grouped;
  }, [enrichedBookings]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="flex flex-col gap-6 h-full">
       {/* Statistics Dashboard */}
       <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Bookings */}
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gradient-to-br from-[#1e2126] to-[#1a1d22] border-[#2E2F33]' : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'} shadow-sm relative overflow-hidden group hover:shadow-md transition-all`}>
             <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-blue-500' : 'bg-blue-400'} group-hover:opacity-30 transition-opacity`}></div>
             <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                   <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <IconWrapper icon={FiCalendar} size={16} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                   </div>
                   <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total</span>
                </div>
                <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-0.5`}>{selectedDateStats.total}</h3>
                <p className={`text-[10px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Bookings</p>
             </div>
          </div>

          {/* Pending Approvals */}
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gradient-to-br from-[#1e2126] to-[#1a1d22] border-[#2E2F33]' : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'} shadow-sm relative overflow-hidden group hover:shadow-md transition-all`}>
             <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-orange-500' : 'bg-orange-400'} group-hover:opacity-30 transition-opacity`}></div>
             <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                   <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                      <IconWrapper icon={FiClock} size={16} className={isDark ? 'text-orange-400' : 'text-orange-600'} />
                   </div>
                   <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Pending</span>
                </div>
                <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-0.5`}>{selectedDateStats.pending}</h3>
                <p className={`text-[10px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Awaiting Approval</p>
             </div>
          </div>

          {/* Active Bookings */}
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gradient-to-br from-[#1e2126] to-[#1a1d22] border-[#2E2F33]' : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'} shadow-sm relative overflow-hidden group hover:shadow-md transition-all`}>
             <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-green-500' : 'bg-green-400'} group-hover:opacity-30 transition-opacity`}></div>
             <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                   <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <IconWrapper icon={FiCheckCircle} size={16} className={isDark ? 'text-green-400' : 'text-green-600'} />
                   </div>
                   <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Active</span>
                </div>
                <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-0.5`}>{selectedDateStats.active}</h3>
                <p className={`text-[10px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Confirmed Seats</p>
             </div>
          </div>

          {/* Utilization */}
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gradient-to-br from-[#1e2126] to-[#1a1d22] border-[#2E2F33]' : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'} shadow-sm relative overflow-hidden group hover:shadow-md transition-all`}>
             <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-[#B59143]' : 'bg-[#FBD282]'} group-hover:opacity-30 transition-opacity`}></div>
             <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                   <div className={`p-2 rounded-lg ${isDark ? 'bg-[#B59143]/20' : 'bg-[#FBD282]/30'}`}>
                      <IconWrapper icon={FiTrendingUp} size={16} className="text-[#B59143]" />
                   </div>
                   <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Usage</span>
                </div>
                <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-0.5`}>{selectedDateStats.utilization}%</h3>
                <p className={`text-[10px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Seat Utilization</p>
             </div>
          </div>
       </div>

       <div className="flex flex-col gap-6">
          {/* Full Width Calendar */}
          <div className="w-full">
             <div className={`p-6 rounded-xl border ${isDark ? 'bg-[#1e2126] border-[#2E2F33]' : 'bg-white border-gray-100'} shadow-sm`}>
                {/* Year Navigation */}
                <div className="flex items-center justify-between mb-3">
                   <button 
                      onClick={() => handleYearChange(-1)}
                      className={`p-1.5 rounded-lg transition-all ${isDark ? 'hover:bg-[#2A3441] text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600'}`}
                   >
                      <IconWrapper icon={FiChevronLeft} size={16} />
                   </button>
                   <h2 className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{viewYear}</h2>
                   <button 
                      onClick={() => handleYearChange(1)}
                      className={`p-1.5 rounded-lg transition-all ${isDark ? 'hover:bg-[#2A3441] text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600'}`}
                   >
                      <IconWrapper icon={FiChevronRight} size={16} />
                   </button>
                </div>

                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                   <button 
                      onClick={() => handleMonthChange(-1)}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553] text-gray-400 hover:text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                   >
                      <IconWrapper icon={FiChevronLeft} size={14} />
                   </button>
                   
                   <select
                      value={viewMonth.getMonth()}
                      onChange={(e) => {
                        const newDate = new Date(viewMonth);
                        newDate.setMonth(parseInt(e.target.value));
                        setViewMonth(newDate);
                      }}
                      className={`px-3 py-1.5 rounded-lg font-bold text-xs outline-none transition-all ${isDark ? 'bg-[#2A3441] text-white hover:bg-[#3A4553]' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
                   >
                      {months.map((month, idx) => (
                        <option key={month} value={idx}>{month}</option>
                      ))}
                   </select>

                   <button 
                      onClick={() => handleMonthChange(1)}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553] text-gray-400 hover:text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                   >
                      <IconWrapper icon={FiChevronRight} size={14} />
                   </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 mb-2 text-center">
                   {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                      <div key={d} className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{d}</div>
                   ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-2">
                   {calendarDays.map((date, idx) => {
                      if(!date) return <div key={`empty-${idx}`} className="h-24" />;
                      
                      const dateStr = toLocalDateString(date);
                      const isSelected = selectedDate ? dateStr === toLocalDateString(selectedDate) : false;
                      const isToday = dateStr === toLocalDateString(new Date());
                      const counts = bookingCountsByDate[dateStr];
                      const hasPending = counts?.pending > 0;
                      const hasActive = counts?.active > 0;
                      
                      return (
                         <button
                            key={idx}
                            onClick={() => setSelectedDate(date)}
                            className={`
                               relative h-24 rounded-lg flex flex-col items-start justify-start p-2 text-xs font-bold transition-all duration-200 hover:scale-[1.02]
                               ${isSelected 
                                  ? 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white shadow-md shadow-[#B59143]/20 scale-[1.02] z-10' 
                                  : isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] text-gray-300 hover:border-[#B59143]' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#B59143]'}
                               ${isToday && !isSelected ? 'ring-2 ring-[#B59143] ring-opacity-50' : ''}
                            `}
                         >
                            {/* Date Number */}
                            <span className={`text-sm font-black mb-1 ${isToday && !isSelected ? 'text-[#B59143]' : ''}`}>{date.getDate()}</span>
                            
                            {/* Booking Counts */}
                            {counts?.total > 0 && (
                               <div className="w-full space-y-0.5">
                                  {counts.pending > 0 && (
                                     <div className={`flex items-center gap-1 text-[9px] font-semibold ${isSelected ? 'text-white' : 'text-orange-600 dark:text-orange-400'}`}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                        <span>{counts.pending} Pending</span>
                                     </div>
                                  )}
                                  {counts.active > 0 && (
                                     <div className={`flex items-center gap-1 text-[9px] font-semibold ${isSelected ? 'text-white' : 'text-green-600 dark:text-green-400'}`}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                        <span>{counts.active} Active</span>
                                     </div>
                                  )}
                               </div>
                            )}
                            
                            {/* Progress Bar */}
                            {counts?.total > 0 && (
                               <div className="absolute bottom-0 left-0 right-0 h-1 flex rounded-b-lg overflow-hidden">
                                  {counts.active > 0 && (
                                     <div 
                                        className="bg-green-500" 
                                        style={{ width: `${(counts.active / counts.total) * 100}%` }}
                                     ></div>
                                  )}
                                  {counts.pending > 0 && (
                                     <div 
                                        className="bg-orange-500" 
                                        style={{ width: `${(counts.pending / counts.total) * 100}%` }}
                                     ></div>
                                  )}
                               </div>
                            )}
                         </button>
                      );
                   })}
                </div>
                
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-[#2E2F33] flex items-center justify-between gap-3">
                   <div className="flex items-center gap-3 text-[10px] font-semibold">
                      <div className="flex items-center gap-1.5">
                         <div className="w-2 h-2 rounded-full bg-green-500"></div>
                         <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Active</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                         <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                         <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Pending</span>
                      </div>
                   </div>
                   
                   {/* Show All Bookings Button */}
                   <button
                      onClick={() => setSelectedDate(null)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                         !selectedDate
                            ? isDark ? 'bg-[#B59143] text-white' : 'bg-[#B59143] text-white'
                            : isDark ? 'bg-[#2A3441] text-gray-400 hover:bg-[#3A4553] hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                   >
                      Show All Bookings
                   </button>
                </div>
             </div>
          </div>

          {/* Filters & Booking List */}
          <div className="flex flex-col gap-6">
             {/* Search & Filters Bar */}
             <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="flex-1 relative">
                   <IconWrapper icon={FiSearch} size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                   <input
                      type="text"
                      placeholder="Search by name, email, or seat..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-10 pr-3 py-2.5 rounded-lg text-sm border outline-none transition-all ${isDark ? 'bg-[#1e2126] border-[#2E2F33] text-white placeholder-gray-500 focus:border-[#B59143]' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#B59143]'}`}
                   />
                </div>

                {/* Floor Filter */}
                <div className="relative min-w-[200px]">
                   <select 
                      value={filterFloor}
                      onChange={(e) => setFilterFloor(e.target.value)}
                      className={`w-full px-3 py-2.5 rounded-lg text-sm border appearance-none outline-none transition-all ${isDark ? 'bg-[#1e2126] border-[#2E2F33] text-white focus:border-[#B59143]' : 'bg-white border-gray-200 text-gray-900 focus:border-[#B59143]'}`}
                   >
                      <option>All Floors</option>
                      {floors.map(f => <option key={f.id}>{f.name}</option>)}
                   </select>
                   <div className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <IconWrapper icon={FiFilter} size={14} />
                   </div>
                </div>

                {/* Status Filter Pills */}
                <div className={`flex p-1 rounded-lg ${isDark ? 'bg-[#1e2126] border border-[#2E2F33]' : 'bg-gray-100'}`}>
                   {['all', 'pending', 'active'].map((s) => (
                      <button
                         key={s}
                         onClick={() => setStatusFilter(s as any)}
                         className={`px-3 py-1.5 text-[11px] font-bold capitalize rounded-md transition-all ${
                            statusFilter === s 
                            ? isDark ? 'bg-[#B59143] text-white shadow-md' : 'bg-white text-[#B59143] shadow-sm'
                            : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'
                         }`}
                      >
                         {s}
                      </button>
                   ))}
                </div>

                {/* Refresh Button */}
                <button 
                   onClick={onRefresh}
                   className={`p-2.5 rounded-lg transition-all ${isDark ? 'bg-[#1e2126] border border-[#2E2F33] text-gray-400 hover:text-white hover:border-[#B59143]' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#B59143]'}`}
                   title="Refresh List"
                >
                   <IconWrapper icon={FiRefreshCw} size={16} />
                </button>
             </div>

              {/* Booking List Header */}
              <div className="flex items-center justify-between">
                 <div>
                    <p className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                       {enrichedBookings.length} {enrichedBookings.length === 1 ? 'booking' : 'bookings'} found
                    </p>
                 </div>
                 
                 <div className="flex items-center gap-3">
                    {/* Bulk Actions - Only show when a specific date is selected */}
                    {selectedDate && enrichedBookings.some(b => b.status === 'pending') && (
                       <div className="flex items-center gap-2">
                          <button
                             onClick={handleRejectAll}
                             className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                isDark 
                                   ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20' 
                                   : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                             }`}
                          >
                             <IconWrapper icon={FiXCircle} size={14} />
                             <span>Reject All</span>
                          </button>
                          <button
                             onClick={handleApproveAll}
                             className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                isDark 
                                   ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20' 
                                   : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                             }`}
                          >
                             <IconWrapper icon={FiCheckCircle} size={14} />
                             <span>Approve All</span>
                          </button>
                       </div>
                    )}
                    
                    <div className="text-right">
                       <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {selectedDate 
                             ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) 
                             : `${months[viewMonth.getMonth()]} ${viewMonth.getFullYear()} - All Bookings`
                          }
                       </h2>
                    </div>
                 </div>
              </div>

              {/* Booking List */}
              {!enrichedBookings.length ? (
                 <div className={`flex-1 flex flex-col items-center justify-center rounded-xl border-2 border-dashed ${isDark ? 'border-[#2E2F33] bg-[#1e2126]/50' : 'border-gray-200 bg-gray-50/50'} min-h-[400px]`}>
                    <div className={`w-16 h-16 rounded-xl ${isDark ? 'bg-[#2A3441]' : 'bg-gray-100'} flex items-center justify-center mb-3`}>
                       <IconWrapper icon={FiAlertCircle} size={28} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
                    </div>
                    <h3 className={`text-base font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1.5`}>No Bookings Found</h3>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                       {searchQuery ? 'Try adjusting your search or filters' : 'No bookings for this date'}
                    </p>
                 </div>
              ) : (
                 <div className="flex flex-col gap-5 overflow-y-auto pr-2 pb-4">
                    {Object.entries(bookingsByDate).map(([date, dateBookings]) => (
                       <div key={date} className={`rounded-xl border ${isDark ? 'bg-[#1e2126] border-[#2E2F33]' : 'bg-white border-gray-100'}`}>
                          {/* Date Header */}
                          <div className={`px-4 py-3 border-b ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-gray-50 border-gray-100'}`}>
                             <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                   <IconWrapper icon={FiCalendar} size={14} className={isDark ? 'text-[#B59143]' : 'text-[#B59143]'} />
                                   <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                   </span>
                                </div>
                                <div className="flex items-center gap-2">
                                   <IconWrapper icon={FiUsers} size={12} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                                   <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {dateBookings.length} {dateBookings.length === 1 ? 'User' : 'Users'}
                                   </span>
                                </div>
                             </div>
                          </div>
                           
                          {/* Users Grid */}
                          <div className="p-4 grid grid-cols-1 gap-3">
                             {dateBookings && dateBookings.length > 0 ? (
                                dateBookings.map(booking => (
                                   <div 
                                      key={booking.id}
                                      className={`group p-3 rounded-lg border transition-all hover:shadow-sm ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] hover:border-[#3A4553]' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}
                                   >
                                      <div className="flex gap-3">
                                         {/* User Avatar */}
                                         <div className={`
                                            relative w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm shadow-sm
                                            ${booking.status === 'pending' 
                                               ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' 
                                               : 'bg-gradient-to-br from-green-400 to-green-600 text-white'}
                                         `}>
                                            <span className="relative z-10">{booking.userFirstName?.[0]}{booking.userLastName?.[0]}</span>
                                            {booking.status === 'pending' && (
                                               <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full animate-pulse"></div>
                                            )}
                                         </div>

                                         {/* Details */}
                                         <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                               <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                  {booking.userFirstName} {booking.userLastName}
                                               </h4>
                                               {booking.status === 'pending' && (
                                                  <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                                     Pending
                                                  </span>
                                               )}
                                            </div>
                                            
                                            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                               <span className={`flex items-center gap-1 font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                  <IconWrapper icon={FiUser} size={10} /> {booking.userRole}
                                               </span>
                                               <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                                               <span className={`font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                  Floor: <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{booking.floorName}</span>
                                               </span>
                                               <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                                               <span className={`font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                  Seat: <span className="font-black text-[#B59143]">{booking.seatName}</span>
                                               </span>
                                            </div>
                                            
                                            {booking.userEmail && (
                                               <p className={`text-[9px] font-medium mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                  {booking.userEmail}
                                               </p>
                                            )}
                                         </div>

                                         {/* Actions */}
                                         <div className="flex gap-1.5 items-center">
                                            {booking.status === 'pending' && (() => {
                                               const isPastDate = new Date(booking.date) < new Date(new Date().setHours(0, 0, 0, 0));
                                               return (
                                                  <>
                                                     <Tooltip label={isPastDate ? "Cannot reject past bookings" : "Reject"} hasArrow placement="top">
                                                        <button 
                                                           onClick={() => !isPastDate && handleReject(booking.id)}
                                                           disabled={isPastDate}
                                                           className={`px-3 py-1.5 rounded-lg font-semibold text-[10px] transition-all flex items-center gap-1 ${
                                                              isPastDate 
                                                                 ? isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                                                 : isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
                                                           }`}
                                                        >
                                                           <IconWrapper icon={FiXCircle} size={12} />
                                                           <span>Reject</span>
                                                        </button>
                                                     </Tooltip>
                                                     <Tooltip label={isPastDate ? "Cannot approve past bookings" : "Approve"} hasArrow placement="top">
                                                        <button 
                                                           onClick={() => !isPastDate && handleApprove(booking.id)}
                                                           disabled={isPastDate}
                                                           className={`px-3 py-1.5 rounded-lg font-semibold text-[10px] transition-all flex items-center gap-1 ${
                                                              isPastDate
                                                                 ? isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                                                 : isDark ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20' : 'bg-green-50 text-green-600 hover:bg-green-100'
                                                           }`}
                                                        >
                                                           <IconWrapper icon={FiCheckCircle} size={12} />
                                                           <span>Approve</span>
                                                        </button>
                                                     </Tooltip>
                                                  </>
                                               );
                                            })()}
                                            {booking.status === 'active' && (() => {
                                               const isPastDate = new Date(booking.date) < new Date(new Date().setHours(0, 0, 0, 0));
                                               return (
                                                  <Tooltip label={isPastDate ? "Cannot cancel past bookings" : "Cancel"} hasArrow placement="top">
                                                     <button 
                                                        onClick={() => !isPastDate && handleReject(booking.id)}
                                                        disabled={isPastDate}
                                                        className={`px-3 py-1.5 rounded-lg font-semibold text-[10px] transition-all flex items-center gap-1 ${
                                                           isPastDate
                                                              ? isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                                              : isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
                                                        }`}
                                                     >
                                                        <IconWrapper icon={FiXCircle} size={12} />
                                                        <span>Cancel</span>
                                                     </button>
                                                  </Tooltip>
                                               );
                                            })()}
                                         </div>
                                      </div>
                                   </div>
                                ))
                             ) : (
                                <div className="text-center py-4">
                                   <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No bookings to display</p>
                                </div>
                             )}
                          </div>
                       </div>
                    ))}
                 </div>
              )}
          </div>
       </div>

       {/* Confirmation Dialog */}
       <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isCentered
       >
          <AlertDialogOverlay className="backdrop-blur-sm bg-black/40">
             <AlertDialogContent className={`rounded-2xl ${isDark ? '!bg-[#1A1C1F] !border !border-[#2E2F33] !text-white' : '!bg-white'}`}>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                   {actionBooking?.action === 'approve' ? 'Approve Booking' : 'Reject Booking'}
                </AlertDialogHeader>

                <AlertDialogBody>
                   Are you sure you want to {actionBooking?.action} this booking?
                   {actionBooking?.action === 'reject' && (
                      <div className={`mt-3 text-sm p-3 rounded-xl ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`}>
                         This will mark the booking as cancelled and free up the seat.
                      </div>
                   )}
                </AlertDialogBody>

                <AlertDialogFooter>
                   <Button ref={cancelRef} onClick={onClose} size="sm" variant="ghost" mr={3}>
                      Cancel
                   </Button>
                   <Button 
                     onClick={confirmAction} 
                     colorScheme={actionBooking?.action === 'approve' ? 'green' : 'red'} 
                     size="sm"
                   >
                      Confirm {actionBooking?.action === 'approve' ? 'Approval' : 'Rejection'}
                   </Button>
                </AlertDialogFooter>
             </AlertDialogContent>
          </AlertDialogOverlay>
       </AlertDialog>
    </div>
  );
};

export default ManagerBookingList;
